import {
  useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction,
} from 'react';
import { Loader2, Maximize2, RotateCw } from 'lucide-react';
import { mountAssembly, type AssemblyView, type ScenePart, type ViewerOverlay } from './assemblyScene';
import type { ClashMarks, StandingOverlap } from './assemblyClash';
import { ViewControls, type ViewAnglesSink } from './ViewControls';
import { MarksButton, ScenePartCards } from './ScenePartCards';
import type { DimensionLineSpec } from './dimensionLines';
import { useDimensionFocus } from './dimensionFocus';
import { compareGroups } from './openaiBlenderApi';
import { quatFromEulerDeg, type Quat } from './assemblyPose';
import { SegmentedToggle } from './SegmentedToggle';
import { PoseToggles } from './PoseToggles';
import { MODES, VIEWPORT_MARK } from './MeshViewer';
import { lightMode, rememberLight, type LightMode, type Shot } from './meshScene';
import { lastDrawMode, rememberDrawMode, type DrawMode } from './meshDrawModes';
import type { PartProfile } from './partProfile';
import { setPartSide, usePartSide } from './partSide';
import { clearPartScene, setPartScene } from './partScene';
import { IS_DEMO } from '../../apiBase';
import { useLanguage } from '../../contexts/LanguageContext';
import { faceKey, type Face } from './faces';
import { faceSaidOf, OrientFaceWhy } from './OrientViews';
import { currentHoleMarks, type HoleMark } from './drillPreview';
import { setPartMarks, usePartMarks } from './partMarks';
import { useOrientAnswer, useOrientRunning } from './orientAnswers';
import { usePhone } from './usePhone';
import type { Assembly, Vec3 } from './assemblyPose';
import { ASKING, type Dims, type Extent, type FacesSaid, type FaceWords } from './meshAnnotations';

// A TEAR-DOWN THAT CAN BE ASKED FOR TWICE. A scene here is let go of by whichever of three
// paths reaches it first — the effect that built it, the swap that replaces it, or the panel
// closing — and freeing a WebGL context twice is not a thing to leave to luck.
const once = (fn: () => void) => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    fn();
  };
};

// ONE PART, STANDING STILL — Phase 2's viewer, and it is Phase 5's scene with nothing moving
// in it.
//
// It was its own viewer (MeshViewer, still used by the bench pages, the modal, the retouch and
// the thumbnails), and the two had drifted into two answers to the same questions: two gizmos,
// two ways of reading where the camera is standing, two ideas of what a measurement looks like.
// A mesh that is judged on one page and assembled on the next has to look the same on both, or
// half of what you learn about it does not carry.
//
// So this mounts `mountAssembly` with a single instance and a single step, which is a part
// standing at the origin: `poseAt` puts a step-0 instance at its seat and leaves it there, so
// there is no `t` here, no transport and nothing to play. What it gets in exchange is the
// whole of the other viewer — the gizmo with the axes lettered, the camera readout, the box
// with its faces named, and the part card with its own switches for the box and the origin
// cross.
//
// WHAT IT KEEPS THAT PHASE 5 DOES NOT HAVE. The box's second row — "wanted height: 733 mm"
// under "3D height: 733 mm" — is this phase's whole question, and the gap between the two rows
// is the answer. It is passed in from here (see `wantedWords`) and nowhere else, so an
// assembly never draws one. And the mesh's raw extents go back up to the page, which is what
// lets it offer a rescale: that offer belongs to this phase alone — a mesh is resized where it
// is made and judged, not from a fitting that happens to be drawing it.

// The one instance, at rest, wherever the panel has put it. Step 0 is "already standing when
// the step opens", which for a viewer with no step is simply: standing.
//
// NOT MOVED, NOT TURNED BY HAND: a part stands where it is put (`standAt`, a joint's place in the
// product) as it was built. A drag on it turns the camera, as a drag anywhere does — a part turned
// on screen stood off its own holes, which read as the holes being wrong.
const still = (pos: Vec3, others: ViewerOther[] = []): Assembly => ({
  instances: [{
    id: 'part',
    itemId: '0',
    parent: null,
    seat: { pos, rot: quatFromEulerDeg(ORIGIN) },
    axis: [0, 0, 1],
    approach: 0,
    step: 0,
    order: 1,
  }, ...others.map((o, k) => ({
    id: o.id,
    itemId: String(k + 1),
    parent: null,
    // WHERE IT STANDS AGAINST THE FIRST PART, which moves them all when it is moved.
    seat: { pos: [pos[0] + o.at[0], pos[1] + o.at[1], pos[2] + o.at[2]] as Vec3, rot: o.rot ?? quatFromEulerDeg(ORIGIN) },
    axis: [0, 0, 1] as Vec3,
    approach: 0,
    step: 0,
    order: k + 2,
  }))],
});

// ANOTHER PART STANDING BESIDE THE FIRST — a joint's (see JointHoles). `at` is its centre
// against the first part's, in millimetres, in the product's frame: where the product built it.
export type ViewerOther = {
  id: string;
  src: string;
  label: string;
  builtMm: { x: number; y: number; z: number } | null | undefined;
  at: Vec3;
  // The other hand of a pair: the same file, reflected.
  mirror?: boolean;
  // HOW IT IS TURNED, where it is not standing as the product built it — a screw laid along its
  // hole. And how it is put in millimetres, where its build measured nothing (see ScenePart).
  rot?: Quat;
  dims?: Dims;
  trueScale?: boolean;
  // A piece of hardware, or a cover: listed under its own header, merged with its likes.
  group?: 'hardware' | 'cover';
};

const ORIGIN: Vec3 = [0, 0, 0];

// HOW FAR IN THE LAST CLOSE LOOK WAS, kept for the page rather than for the part.
//
// A zoom is a working distance. Somebody at ×5 on a leg's hole is at ×5 because that is the
// size the thing they are checking is; the next part is the next one to check, and being put
// back at ×1 on every arrow press means finding the spot again by hand each time.
//
// A MULTIPLE AND NOT A DISTANCE, which is what makes it carry between parts at all: the scene
// reports zoom as a multiple of the distance that frames THIS body at THIS angle (see
// `reportView`), so ×5 means the same thing on a leg and on a screw.
//
// ONLY WHERE A MESH IS BEING JUDGED — the expanded view and the bench pages, which is what
// `topology` marks. The phase's own panel is a strip you glance at to see whether the right
// part came back, and a strip opening at ×5 shows a fragment of one.
//
// A plain variable, like the draw mode and the lighting: one viewer is on screen at a time and
// the next to mount reads it in its initialiser. Written down nowhere — where you last had the
// wheel is not a fact about a document.
let lastZoom = 1;


// WHERE THE CAMERA WAS STANDING, AND HOW THE PART WAS PUT DOWN — kept for the page, like the
// zoom above and for a stronger reason.
//
// These were a ref inside the component, which made them survive one thing: a new mesh loaded
// into the SAME viewer. The panel's viewer and the modal's are two mountings, and expanding
// unmounts one and mounts the other — so every trip in or out of the big view threw the
// camera and the pose away and framed the part afresh. Which is the one moment they matter
// most: you expand a part BECAUSE of what you were looking at, and you come back out to carry
// on with it.
//
// KEYED, and spent only on a match. The key is the run (see keepView), so the same object
// opened big is the same view, and stepping to another part frames it as it always did.
//
// A plain variable for the same reason lastZoom is one: at most one of these viewers is on
// screen at a time, and where the camera was left is not a fact about a document.
let held: { key: string; at: Shot['at']; to: Shot['to']; zoom: number } | null = null;

// HOW TALL A PART IS DRAWN, wherever it is drawn full size — the parts page, the 3D phase's
// panel, and the modal that opens over either of them.
//
// ONE VALUE, BECAUSE IT IS ONE JUDGEMENT. What is read off this viewer is a shape and the box
// round it, and the answer somebody gives after looking depends on how big it was: a part
// judged in the panel and then opened large should not be two different objects. The three
// call sites had 24rem, 32rem and 40vh between them, which on a laptop meant the modal — the
// one place built for looking closely — was the smallest of the three.
//
// A FIXED HEIGHT AND NOT A SHARE OF THE VIEWPORT, so it does not depend on which window the
// reader happens to have. The modal that holds one scrolls (`overflow-y-auto`), so a short
// screen gives up the thumbnails above rather than the mesh.
export const VIEWER_BOX = 'h-[32rem] w-full';

export function PartViewer({
  src,
  dims,
  estimated = false,
  trueScale,
  builtMm,
  label,
  onMeasure,
  onProfile,
  onExpand,
  onStats,
  pieceWanted,
  topology = false,
  lights = false,
  // The two meshes that are one thing to look at — see MeshViewer's keepView, which this
  // only carries.
  keepView,
  sides = false,
  explodable = false,
  pieceLabel,
  pieceOrder,
  corner,
  left,
  orientJobId,
  contactFace,
  controlsAbove: askedAbove = false,
  controlsBeside: askedBeside = false,
  dimensionLines,
  others,
  holeMarks,
  bare = false,
  apart,
  standAt,
  keepSolid,
  highlight,
  moving,
  overlays,
  collide,
  clashPaint,
  className = '',
}: {
  src: string;
  // What the manual says this part measures. Drawn as the second row of every edge label —
  // the claim the mesh is being checked against.
  dims?: Dims;
  // WHETHER THAT CLAIM WAS REASONED RATHER THAN READ — estimated off a photograph against a
  // part that WAS measured, or off the standard a piece of hardware is made to. It earns a ~
  // in front of the number, the same mark the parts table puts on the same fact.
  //
  // A flag and not the size record, because `dims` is deliberately the three numbers and
  // nothing else: whose answer they are is a fact about the record, and the caller has it.
  estimated?: boolean;
  // The file is already the part at true scale (a CAD conversion), so no printed size has to
  // anchor it.
  trueScale?: boolean;
  // AND WHAT THE BUILD MEASURED, where the run measured itself — the real millimetres of this
  // file, for a part the manual never dimensioned. See builtBoxOf, and mmScale, which asks the
  // drawing first.
  builtMm?: { x: number; y: number; z: number } | null;
  // What to call the part on its card. The card is the same one the assembly phase draws, so
  // the switches on it are the same switches.
  label?: string;
  // The mesh's extents in the FILE's own units, handed up so the page can offer a rescale.
  onMeasure?: (size: Extent) => void;
  // AND ITS SHAPE INSIDE THOSE EXTENTS — see partProfile, and holePlace, which is the only
  // thing that reads it. Given, the scene samples it; absent, it does not, and every hole on
  // this part is placed against the box as before.
  onProfile?: (profile: PartProfile) => void;
  onExpand?: () => void;
  // HOW BIG THE MESH IS — its vertices and triangles, counted once it has loaded. For a caller
  // that shows them with its other facts about the model (see MeshModal).
  onStats?: (stats: { vertices: number; triangles: number }) => void;
  // WHAT EACH OF THE FILE'S OWN MESHES IS SUPPOSED TO MEASURE, by name — the "wanted" row on a
  // mesh's box. Absent, every mesh's row reads "?". See partSizeOfMesh.
  pieceWanted?: (mesh: string) => { x: number | null; y: number | null; z: number | null } | null;
  // WHETHER HOW THE MESH IS BUILT IS ON OFFER: the draw-mode toggle and the counts. Off in
  // the phase's own panel, where the question is "is this the right part at the right size";
  // on where a mesh is being judged, which is the expanded view and the bench pages. A
  // reconstruction is judged on where it spent its triangles, and the texture is very good at
  // hiding that.
  topology?: boolean;
  // AND WHETHER THE LIGHTING IS: even against studio. Same split, different question — even
  // is the one that answers "is this the colour it was matched to", and asking it means
  // getting close to one part.
  lights?: boolean;
  // WHAT MAKES TWO PICTURES THE SAME THING TO LOOK AT — two meshes in this viewer, or this
  // viewer and another mounting of it. While the key is unchanged the camera, the zoom and
  // the part's pose carry: a new `src` keeps them rather than framing afresh, and so does a
  // fresh MOUNT, which is what expanding into the modal and closing it again are. Absent,
  // every swap frames anew, which is what it always did. See `held` and `lastPose`.
  keepView?: string;
  // WHETHER THIS PART COMES IN TWO HANDS — a left/right mirror pair (see PartItem.symmetry).
  //
  // Given, the viewer offers a Left/Right toggle and draws the reflected mesh for one of them.
  // It is a way of LOOKING at the part and not a second part: there is one file, one set of
  // holes and one scale, so a drill, a fill or a rescale done on either hand is done on both
  // by construction rather than by being copied across. Their only difference is the
  // reflection, which is what makes a pair a pair.
  sides?: boolean;
  // WHETHER WHAT IS ON SCREEN IS AN ASSEMBLY OF SEPARATE THINGS — the whole product, whose
  // meshes are its parts. Only then is "exploded" a question: everywhere else the file's several
  // meshes are how ONE object was built, and spreading them apart takes the object to bits
  // nobody assembled. Off by default, so a caller that has not thought about it does not offer
  // the switch.
  // 'axis' for hardware built together: apart along its one axis — see explodeShifts.
  explodable?: boolean | 'axis';
  // HOW A MESH'S ROW IS NAMED in the parts card, where that differs from its name — see ScenePartCards.
  pieceLabel?: (name: string) => string;
  // The order its rows are listed in — see ScenePartCards.
  pieceOrder?: string[];
  // A control over the white in the top left, for the one switch that is about this mesh as
  // an asset rather than about its geometry: whether it is the part's model in the Parts DB.
  corner?: ReactNode;
  // IN THE ROOM ON THE PICTURE'S LEFT, half way up, where the controls column takes the right (see
  // `beside`); under the picture where there is no such room.
  left?: ReactNode;
  // WHICH MESH THE ORIENTATION ANSWER ON SCREEN IS ABOUT, when there is one. Given, the six
  // words on the box each gain a second row saying what the model said about that face, red
  // where the applied turn disagrees, and pressing one opens why — see orientAnswers, which
  // is where the answer is published, and OrientViews, which explains it.
  //
  // AN ID AND NOT THE ANSWER ITSELF, because the answer is held by the controls UNDER this
  // viewer (MeshControls) and the two are siblings with a modal between them. Absent
  // everywhere else — the catalog's viewer draws the same box and has no such question.
  orientJobId?: number;
  // THE FACE THAT TOUCHES WHAT THE PART COVERS — a cover built contact face down (see builtDown),
  // said on that face's plate.
  contactFace?: keyof FaceWords;
  // THE CAMERA READOUT, THE POSE, THE DRAW MODE AND THE MARKS SWITCH IN A ROW ABOVE THE
  // VIEWPORT rather than over its corners — for the expanded view, where the mesh is being
  // judged and four boxes over the picture are four places it cannot be seen.
  controlsAbove?: boolean;
  // OR IN A COLUMN BESIDE IT, the assembly phase's arrangement (Louis 2026-10-28): the camera
  // readout, the draw mode, the marks switch and the cards right of the picture, which keeps the
  // pose alone on it. The viewport keeps its height and gives the column its width.
  controlsBeside?: boolean;
  // WHERE A WRITTEN MODEL'S DIMENSIONS ARE MEASURED — see dimensionLines. Each group's lines are
  // shown with its measured box: a mesh's when its name is picked in the cards, the whole
  // object's with the part's own box; and the dimension a field below is focused on, always.
  dimensionLines?: Pick<DimensionLineSpec, 'lines' | 'about'> | null;
  // THE OTHER PARTS OF A JOINT, standing where the product built them. Absent everywhere but
  // the joint view: this is otherwise one part.
  others?: ViewerOther[];
  // THE MARKS TO DRAW, where the caller has its own — a joint's holes, each on its part (see
  // HoleMark.on). Absent, the marks the drill form and the holes table publish.
  holeMarks?: () => HoleMark[];
  // NOTHING DRAWN ABOUT THE PARTS — boxes, rulers, face names, origin crosses, the gizmo, outlines,
  // measurements — while the caller plays a movement; each back as its switch stands once it ends.
  // The switches are not touched. (The caller's hole marks are its own: see holeMarks.)
  bare?: boolean;
  // ASSEMBLED OR EXPLODED, where the caller spreads its own parts apart (a joint's). Offered on
  // the pose toggle the file's own pieces use.
  apart?: { on: boolean; set: (on: boolean) => void };
  // WHERE THE FIRST PART STANDS, where the caller says — a joint's, in the product's own frame
  // about its middle, so the origin is the product's as "Whole product" draws it. Followed as it
  // moves (the joint spreading apart); a drag still moves it from there.
  standAt?: Vec3;
  // Which parts stay solid seen through — see the 'xray' draw mode.
  keepSolid?: (instanceId: string) => boolean;
  // PARTS OUTLINED BESIDE THE SELECTED ONE, by instance id — what a row of the caller's table
  // points at (a joint's covers, see JointHoles).
  highlight?: string[];
  // AND WHAT IS MOVING, outlined the same way — even bare, while it plays (a joint's sub-step).
  moving?: string[];
  // SOLIDS LAID OVER THE PART, in its own file frame — a product's overlaps. See AssemblyView.overlays.
  overlays?: readonly ViewerOverlay[];
  // PAIRS OF INSTANCES MEASURED FOR OVERLAP where the picture stands — the covers against the
  // parts they go on (see coverCollisions). Measured again when `key` changes, and on a new scene.
  collide?: {
    key: string;
    pairs: readonly (readonly [string, string])[];
    onFound: (found: StandingOverlap[]) => void;
  } | null;
  // AND THE FACES PAINTED RED, by instance — see AssemblyView.clashMarks. Null paints none.
  clashPaint?: ClashMarks | null;
  className?: string;
}) {
  const { t, language } = useLanguage();
  // AND ALWAYS ON A PHONE: there the four boxes over the corners are wider than the picture
  // together, and they were drawn over one another as well as over the part.
  const controlsAbove = usePhone() || askedAbove;
  const beside = !controlsAbove && askedBeside;
  // The cards in a list of their own, out of the picture: above it, or beside it.
  const outside = controlsAbove || beside;
  // TWO MOUNT POINTS, ONE OVER THE OTHER, and a scene in at most one of them at a time.
  //
  // THE SCENE THAT IS UP STAYS UP WHILE ITS REPLACEMENT LOADS. Every edit to this mesh —
  // drilling a hole, scaling it, closing a surface — comes back as a new URL, and a new URL
  // was a scene torn down at the instant it was asked for and several seconds of white with a
  // spinner in the middle of it. Which is the wrong picture in the most literal sense: the
  // part had not gone anywhere, and the one thing a reader wants to do with a drilled part is
  // compare it with the one they were looking at.
  //
  // The layers are `invisible` and not `hidden`: the scene sizes itself off its element, and
  // `display: none` is a canvas told it is nought by nought.
  const hosts = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
  const [shown, setShown] = useState(0);
  const shownAt = useRef(0);
  // WHAT IS ON SCREEN, and — while a swap is in flight — what is loading behind it. Held as
  // their own unmount functions rather than left to the effect's cleanup, which React runs the
  // moment the URL changes and which is exactly the tear-down being deferred.
  const standing = useRef<(() => void) | null>(null);
  const inFlight = useRef<(() => void) | null>(null);
  const view = useRef<AssemblyView | null>(null);
  // WHAT THE LAST REPORT SAID THE ZOOM WAS — a multiple of the distance that frames this body
  // in THIS box, which is the only form of it that can cross to a box of another size. The
  // panel is a few hundred pixels and the modal is a screen; the same camera distance is a
  // different picture in each, and the same multiple is the same picture.
  const zoomNow = useRef(1);
  // The key this viewer is currently keeping its view under, for the tear-down below — which
  // runs once, at the end, and would otherwise close over the key from the first render.
  const keyNow = useRef(keepView);
  const viewAngles: ViewAnglesSink = useRef<((v: { deg: [number, number, number]; zoom: number; centre: [number, number, number] }) => void) | null>(null);
  // WHAT IS ON SCREEN, and not what has been asked for. `state` is about the scene the reader
  // is looking at — so it stays 'ready' all through a swap, and the spinner and the failure
  // message stay off a picture that is perfectly good.
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  // AND WHETHER A REPLACEMENT IS COMING. Nothing is drawn from it: it is here so that a
  // control that wants to know whether this viewer is busy has one thing to ask.
  const [swapping, setSwapping] = useState(false);
  // HOW MANY SCENES THIS VIEWER HAS PUT ON SCREEN, which is what every setting below watches
  // to know it must be sent again.
  //
  // It was `state` — the effects re-applied whenever the viewer went loading → ready, which
  // was every rebuild, because a rebuild blanked the panel. It does not any more: the mesh on
  // screen stays up while its replacement loads and `state` never leaves 'ready', so nothing
  // re-sent, and a part came back from a drill with its rulers, its face plates and its
  // origin cross switched on again — settings somebody had turned off, restored by an edit
  // that had nothing to do with them.
  //
  // A COUNT AND NOT A FLAG: two scenes in a row is two ticks, where a boolean that was already
  // true says nothing. And it ticks where the swap happens rather than where a scene loads, so
  // it counts what is being LOOKED at — a build that failed, or one abandoned mid-flight,
  // never reaches here and never re-sends anything to a viewer it is not in.
  const [scenes, setScenes] = useState(0);
  // THE MARKS ARE SHARED WITH EVERY OTHER VIEWER OF THIS PART — see partMarks. The panel and
  // the modal are two mountings of this component, and holding these here meant hiding the
  // measurements in one and opening the other brought them straight back.
  const { rulersOff, facesOff, boxesOff, gizmoOn, exploded } = usePartMarks();
  const setGizmoOn = (v: boolean) => setPartMarks({ gizmoOn: v });
  const setRulersOff = (v: string[]) => setPartMarks({ rulersOff: v });
  const setFacesOff = (v: string[]) => setPartMarks({ facesOff: v });
  const setBoxesOff = (v: string[]) => setPartMarks({ boxesOff: v });
  // AND THE OTHER PARTS' SWITCHES, a joint's (see `others`): this viewer's own, and ON only once
  // pressed — four boxes and their rulers over one joint is a picture of boxes. The part's own
  // stay in the shared store, as every other viewer of it has them.
  type Kind = 'rulers' | 'faces' | 'boxes';
  const [othersOn, setOthersOn] = useState<Record<Kind, string[]>>(
    { rulers: [], faces: [], boxes: [] });
  //
  // IN A JOINT THE FIRST PART IS ONE OF THEM: every part starts bare, and none borrows the one-part
  // viewer's switches — which start on, and would light whichever part happens to come first.
  const joint = !!others?.length;
  const otherIds = [...(joint ? ['part'] : []), ...(others ?? []).map((o) => o.id)];
  const offOf = (kind: Kind, stored: string[]) => [
    ...(joint ? [] : stored), ...otherIds.filter((id) => !othersOn[kind].includes(id)),
  ];
  const onOf = (kind: Kind, stored: string[]) => [
    ...(joint || stored.includes('part') ? [] : ['part']), ...othersOn[kind],
  ];
  const switchOf = (kind: Kind, stored: string[], set: (v: string[]) => void) => (id: string) => {
    if (id === 'part' && !joint) {
      set(stored.includes(id) ? stored.filter((x) => x !== id) : [...stored, id]);
      return;
    }
    setOthersOn((was) => ({
      ...was,
      [kind]: was[kind].includes(id) ? was[kind].filter((x) => x !== id) : [...was[kind], id],
    }));
  };
  const [hidden, setHidden] = useState<string[]>([]);
  // AND ITS OWN MESHES, where the file holds several — a row each under the card, each with its
  // own eye. Per file: another mesh on screen is another list, and nothing hidden carries over.
  const [pieces, setPieces] = useState<string[]>([]);
  const [piecesOff, setPiecesOff] = useState<string[]>([]);
  // And which of them wear a measured box of their own — the name in the row is its switch.
  const [piecesBoxed, setPiecesBoxed] = useState<string[]>([]);

  // (Whose faces are named lives in the same store: the box the words hang on is the box the
  // measurements hang on, and it is drawn while either of the two is on — so both off is the
  // way to look at the mesh itself, which is half of what this viewer is for.)
  // Whether the card is open on its numbers, and what those numbers are. Both are this
  // component's: there is no fitting behind them.
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    setPieces([]);
    setPiecesOff([]);
    setPiecesBoxed([]);
  }, [src]);
  // WHICH FACE'S NAME WAS PRESSED, if any — the one thing on this viewer that is a question
  // about an ANNOTATION rather than about the part.
  const [askedFace, setAskedFace] = useState<string | null>(null);
  // AND WHAT THE ORIENTATION CALL SAID ABOUT THIS MESH, if anything. Read from the store the
  // controls publish into rather than passed down, because the controls are this viewer's
  // sibling — see orientAnswers.
  const orient = useOrientAnswer(orientJobId);
  // THE HOLE MARKS ARE NOT READ HERE: the scene asks for them itself, every frame (see
  // mountAssembly's `holeMarks`), because a set of marks that follows a table row by row
  // should not depend on a subscription, a re-render and an effect all firing.
  // Whether that answer is currently being asked for again, in which case none of it is
  // shown — see the effect that sends it to the box.
  const orientAsking = useOrientRunning(orientJobId);
  const faceSaid = useMemo(() => {
    const byFace = faceSaidOf(orient);
    const out: FacesSaid = {};
    for (const [face, said] of Object.entries(byFace)) {
      // "says: back" — the model's word for this face, under the app's own name for it. The
      // two rows are one sentence when they agree and a contradiction when they do not, which
      // is the whole reason they are on one plate.
      out[face as keyof FacesSaid] = {
        text: `${t('notice.3d.orientFaceSays')} ${t(faceKey(said.shows as Face)).toLowerCase()}`,
        fits: said.fits,
      };
    }
    if (contactFace) out[contactFace] = { text: t('notice.covers.contactFace'), fits: true };
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orient, language, contactFace]);
  // (How far in this viewer opens is `lastZoom`, read where each SCENE is built rather than
  // held in a ref — see the effect below, which is where the reason lives.)
  // Textured or wireframe, and it opens on whatever was last asked for — see lastDrawMode,
  // which is where the argument for remembering it lives.
  const [mode, setMode] = useState<DrawMode>(lastDrawMode);
  const pickMode = (next: DrawMode) => { rememberDrawMode(next); setMode(next); };
  // WHICH HAND OF A PAIR IS ON SCREEN. Left is the file as it stands and right is its
  // reflection — DECLARED, not discovered: a reconstruction is made from one photograph of one
  // unit, and nothing in the picture says which of the two was on the table. Either hand is
  // the mirror of the other, so nothing is lost by saying THE FILE IS THE LEFT UNIT and
  // holding to it.
  //
  // It is held to. Every hole is read in the left unit's frame (StepHole.side), the drill
  // refuses to cut while the reflection is up, and left and right mean the same two sides here
  // as they do on the mesh's own faces (faces.ts) and on a classified photograph
  // (_GALLERY_VIEW_PROMPT) — the side you see on your left, standing in front of the finished
  // product. What the toggle is for is the fact that there ARE two: a reader fitting the
  // right-hand rail can see the right-hand rail.
  //
  // HELD OUTSIDE THIS COMPONENT, in partSide. It was local state, and this viewer is mounted
  // twice over one part — the panel and the modal — so each had its own hand and switching in
  // one left the other where it was. The controls beside it also read it now: a pair's holes
  // come in two sets and the list has to show the one on screen.
  const side = usePartSide();
  // Opened at whatever the last viewer was left on — the choice outlives the viewer it was
  // made in, see lightMode.
  const [lit, setLit] = useState<LightMode>(lightMode);
  // WHERE THE PART IS PUT: where the caller stands it, or the origin.
  const [seat, setSeat] = useState<Vec3>(() => standAt ?? ORIGIN);
  const standKey = standAt?.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (standAt) setSeat(standAt); }, [standKey]);
  const othersKey = (others ?? []).map((o) => `${o.id}:${o.src}:${o.at.join(',')}:${o.rot?.join(',') ?? ''}`).join(';');
  // Which parts, and not where: moving them apart re-seats them, it does not load them again.
  const othersIds = (others ?? []).map((o) => `${o.id}:${o.src}:${o.label}:${!!o.mirror}`).join(';');
  // Which of those the scene on screen was built with — see `collide`.
  const sceneOthers = useRef<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const asm = useMemo(() => still(seat, others), [seat, othersKey]);
  // Read inside the mount effect, which must not re-run when the pose changes — a new scene
  // per typed degree is a mesh re-downloaded per typed degree.
  const latest = useRef(asm);
  latest.current = asm;

  // HOW MANY TIMES THIS MESH HAS BEEN ASKED FOR AGAIN. Nought until somebody presses the
  // button below; every press past that puts a new number on the end of the URL, which is
  // the whole mechanism — see `url`.
  const [attempt, setAttempt] = useState(0);

  // Keyed by the URL alone, so nothing about the words on screen rebuilds a scene that is
  // holding a mesh. The labels below are baked in at mount for the same reason the assembly's
  // are: a gizmo's words are geometry, and the language is the only thing that may change them.
  //
  // AND BY THE ATTEMPT, which is how the retry works: a different URL is a different scene,
  // so the effect below tears the old one down and builds another. A cache buster and not a
  // flag, because the fetch is the thing being retried — the browser holds the address, and
  // asking for the same one again is how you get the same failure back.
  // BY ITS THREE NUMBERS AND NOT BY THE OBJECT. `builtMm` is built fresh by its caller on every
  // render — it is folded out of the run's measures, not stored — so as a dependency it is never
  // equal to itself: the parts list was rebuilt every frame, the scene remounted on every rebuild,
  // and the viewer flickered on a page nobody was touching. A value this size is compared by
  // value; there is nothing to memoise upstream and nothing for a caller to remember to do.
  const builtKey = builtMm ? `${builtMm.x}:${builtMm.y}:${builtMm.z}` : '';
  const parts = useMemo<ScenePart[]>(
    () => [{
      instanceId: 'part',
      url: attempt ? `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}` : src,
      dims: dims ?? null,
      trueScale: !!trueScale,
      builtMm: builtMm ?? null,
      label: label ?? '',
      // The reflection, for the right hand of a pair — and never for a part that has no
      // second hand, whatever the toggle was left on when the selector moved: `sides` going
      // false is a different part, and it must not inherit the last one's handedness.
      mirror: sides && side === 'right',
    }, ...(others ?? []).map((o) => ({
      instanceId: o.id,
      url: o.src,
      builtMm: o.builtMm,
      label: o.label,
      mirror: !!o.mirror,
      dims: o.dims ?? null,
      trueScale: !!o.trueScale,
    }))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src, trueScale, builtKey, label, attempt, sides, side, othersIds],
  );

  const words = useMemo(() => ({
    // NO "=0" ANY MORE. The suffix said where the arrow came OUT of — the one point in the
    // picture where all three measurements read zero — and it was true while the arms stood at
    // the origin. They are pinned in a corner now and start nowhere in particular; the origin
    // is marked by its own black cross instead. What is left is the axis and what the arm is
    // worth, which is all a corner legend can honestly claim.
    x: t('notice.3d.width'),
    y: t('notice.3d.depth'),
    z: t('notice.3d.height'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);
  const extentWords = useMemo(() => ({
    width: t('notice.3d.width'),
    depth: t('notice.3d.depth'),
    height: t('notice.3d.height'),
    unknown: t('notice.3d.unknown'),
    bottomWidth: t('notice.3d.bottomWidth'),
    topWidth: t('notice.3d.topWidth'),
    bottomDepth: t('notice.3d.bottomDepth'),
    topDepth: t('notice.3d.topDepth'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);
  const wantedWords = useMemo(() => ({
    // One word for all three: the axis is on the line above, which is what being under it
    // means. See notice.3d.wanted.
    width: t('notice.3d.wanted'),
    depth: t('notice.3d.wanted'),
    height: t('notice.3d.wanted'),
    unknown: t('notice.3d.unknown'),
    // A ~ WHERE THE SIZE WAS REASONED RATHER THAN READ — off a photograph against a part that
    // was measured, or off the standard a piece of hardware is made to. The same mark the
    // parts table puts on the same fact, so a number is not an estimate on one screen and a
    // measurement on the next.
    about: estimated ? '~' : '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language, estimated]);
  const faceWords = useMemo(() => ({
    // The assembly phase's own six words, and deliberately not a second set: a face named
    // "Top" here and something else there would be two vocabularies for one part.
    right: t('notice.assembly.faceRight'),
    left: t('notice.assembly.faceLeft'),
    back: t('notice.assembly.faceBack'),
    front: t('notice.assembly.faceFront'),
    top: t('notice.assembly.faceTop'),
    bottom: t('notice.assembly.faceBottom'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);

  useEffect(() => {
    // A NEW SCENE IS A NEW FRAMING, AND THE ZOOM HAS TO BE PUT BACK INTO IT.
    //
    // The latch below applies the remembered distance once and then leaves it alone —
    // otherwise every report of the wheel turning would fight the wheel. It belongs to THIS
    // SCENE and not to the component, which is the whole of the bug it replaces.
    //
    // Two scenes are alive at once here. A new mesh is built into the free layer while the old
    // one is still on screen (see below), and the old one goes on reporting — a wheel still
    // damping, a mark redrawn. Held on the component, the first of those reports closed the
    // latch, and the new scene's own first report then found it shut and left the camera at
    // the x1 it was framed at. Twenty minutes of work at x4.63 on a leg, and the part comes
    // back whole and far away.
    //
    // The angle survived it (that is shared and stored, see meshView), which is what made this
    // read as "the zoom was lost" rather than "the view reset": only the one fact nobody was
    // putting back went missing.
    //
    // `want` is read here, once, for the same reason — the scene's own first report overwrites
    // lastZoom a frame later, so a value read inside the callback would be the framing it is
    // trying to correct.
    let zoomPut = false;
    const want = lastZoom;
    // AND THE SCENE THIS EFFECT BUILT, held apart from `view.current`.
    //
    // `view.current` is the viewer's scene, and during a swap it is still the OLD one: the
    // branch below only clears it when there is nothing on screen to keep. So the new scene's
    // first report found a scene, closed the latch, and moved the camera of the one that was
    // about to be thrown away. Every earlier attempt at this bug missed for that reason — the
    // latch was made per-effect, then per-scene, and both times it was closed by a report
    // about a scene that was not this one.
    let mineView: (typeof view)['current'] = null;

    // INTO THE FREE LAYER, over the one holding the scene that is up. With nothing up — the
    // first mesh, or the one after a failure — there is nothing to keep, so it goes into the
    // layer already shown and the swap below is a flip to where we already are.
    const before = standing.current;
    const slot = before ? 1 - shownAt.current : shownAt.current;
    const el = hosts[slot].current;
    if (!el) return;
    if (before) {
      // NOT 'loading': that word drives the spinner, and there is a part on screen. What is
      // loading is its replacement, which is the button's business — every control that
      // rebuilds this scene has its own spinner on it — and not a curtain over the picture.
      setSwapping(true);
    } else {
      if (view.current) clearPartScene(view.current);
      view.current = null;
      setState('loading');
    }
    let mine: (() => void) | null = null;
    // Taken before the scene that holds it is replaced, because after that there is
    // nothing to ask.
    if (keepView && view.current) {
      held = { key: keepView, ...view.current.viewpoint(), zoom: zoomNow.current };
    }

    const raw = mountAssembly(el, parts, {
      explodeAlong: explodable === 'axis' ? 'z' : undefined,
      keepSolid,
      // The holes marked on the part — typed in the drill form, or picked out of the
      // reading beside it — asked for on every frame.
      holeMarks: holeMarks ?? currentHoleMarks,
      // ONE PART, LOOKED AT — so turning it turns every picture of a mesh in the app, which
      // is what the thumbnails under this viewer already follow. See meshView.
      sharedView: true,
      // ONE PART, AND IT IS SOLID: there is nowhere inside it to look from. See keepOut. IN THE DEMO
      // ONLY: in the app the wheel goes where it is turned — a joint is several parts, and the wall
      // and the aim were the first one's, so a rail pushed off it could be neither aimed at nor neared.
      keepOut: IS_DEMO,
      // THE BOX ON ITS OWN SWITCH — the part's name in the card — so the ruler and the tag are
      // about the words on it and not about whether it is there. See `boxes`.
      boxSwitch: true,
      pieceWanted,
      onPieces: (_, names) => setPieces(names),
      onReady: (v) => {
        mineView = v;
        view.current = v;
        applyMarks.current(v);
        // WHICH PARTS THIS SCENE HOLDS — see `collide`, which must not measure one short of them.
        sceneOthers.current = othersIds;
        // AND PUBLISHED, so the controls beside this viewer can ask the loaded mesh a question
        // — how much wood a bore crosses, which nothing but the geometry can answer. See
        // partScene, and AssemblyView.thickness.
        setPartScene(v);
        // Step 1 with a step-0 instance, which is poseAt's way of saying "already standing":
        // its k is 1, so it sits at its seat with none of its approach left to travel.
        v.show(latest.current, 1, 1);
        v.frame(latest.current, 1);
        // AND BACK WHERE THE LAST ONE WAS STANDING — after the framing and not before it.
        //
        // `frame` places the camera itself: home's distance, at the angle the parts are
        // shared at. It ran AFTER this restore, which was done from `onState` a moment
        // earlier, so the exact camera being put back was wiped by the framing every time and
        // what actually carried across a swap was the shared angle plus a zoom multiple that
        // shrank as it went. Here it is the last word, and it carries the pan with it — the
        // whole camera, which is what "the same view" means.
        if (keepView && held?.key === keepView) {
          // The direction and the point it looks at, verbatim; then the distance as a
          // MULTIPLE of what frames the body in this box. In the same box those are the same
          // thing — zoomTo puts back exactly the distance the multiple was read at — and in a
          // box of another size only the multiple means anything, which is what makes this
          // work between the panel's viewer and the modal's.
          v.aimAt({ at: held.at, to: held.to });
          if (held.zoom > 0) v.zoomTo(held.zoom);
          // And the zoom latch has nothing left to put back.
          zoomPut = true;
        }
      },
      axisWords: words,
      extentWords,
      faceWords,
      wantedWords,
      light: lit,
      onStats: (counts) => onStats?.(counts),
      onView: (v) => {
        zoomNow.current = v.zoom;
        if (topology) lastZoom = v.zoom;
        viewAngles.current?.(v);
        // AND THE FIRST REPORT IS WHERE A REMEMBERED ZOOM IS PUT BACK, rather than straight
        // after mounting: the scene frames the part before it says anything, so this is the
        // first moment there is a fitted distance for a multiple to be a multiple OF. Once,
        // and only where the wheel had actually been left somewhere.
        // AND NOT BEFORE THIS SCENE IS READY. It reports its framing before onReady runs, so
        // the latch must not close on a report there is nothing to answer with — and what
        // answers is `mineView`, this effect's own scene, never `view.current`, which during a
        // swap is still the scene being replaced.
        if (!zoomPut && mineView) {
          zoomPut = true;
          // THE DISTANCE AND NOTHING ELSE. Which way round the part is standing is a fact
          // about the part — the scene takes that from the shared angle as it frames — and
          // this only moves the camera along the line it is already on.
          //
          // It went through `look` with the angle just reported and the part's centre, which
          // put back three ROUNDED degrees and, worse, a zoom measured one way and applied
          // another: `look` fitted the body as seen from where it stands, `reportView` divided
          // by the square-on distance (it no longer does — see fitHere), and the two differed
          // by up to the part's own diagonal.
          // So the number came back smaller than it went in, and the viewer dutifully put
          // THAT back next time — 2.55, 1.59, 0.99, 0.62, each a constant factor below the
          // last. Toggling between a run's two passes walked down that ladder a rung a press.
          if (topology && Math.abs(want - 1) > 0.01) mineView.zoomTo(want);
        }
      },
      // A click on the part opens its card's numbers, and a click on the white closes them —
      // the same gesture the assembly phase answers, drawn by the same card.
      //
      // AND A SECOND CLICK ON THE PART CLOSES NOTHING, which matters more here than there: a
      // press that goes nowhere is a click, this is the one viewer where the part is grabbable
      // only WHILE it is selected, and a drag that came up without moving would have taken the
      // selection away with it — leaving the next press orbiting the camera instead. The white,
      // the panel's cross and the card's name are the ways out.
      onPick: (pick) => { setPicked(pick ? pick.instanceId : null); },
      onMeasured: (_id, size) => onMeasure?.(size),
      onProfiled: onProfile ? (_id, profile) => onProfile(profile) : undefined,
      // A PRESS ON ONE OF THE SIX WORDS asks about that word. Which face it is comes back by
      // name; what to say about it is the orientation answer, held elsewhere and read below.
      // Set unconditionally, so the plates are pressable whenever there is an answer — the
      // modal renders nothing for a face with nothing said about it.
      onPickFace: (_id, face) => setAskedFace(face),
      // THE SWAP HAPPENS HERE, on the first word this scene says about itself that is not
      // 'loading' — which mountAssembly says as it starts, about the scene now behind the one
      // on screen, and which is not news to a viewer that already asked for it.
      onState: (s) => {
        if (s === 'loading') return;
        // AND ONLY IF THIS IS STILL THE SCENE BEING WAITED FOR. A build that was abandoned
        // — a second edit landing on top of it, or the panel closing — must not report: it
        // would take down the part on screen and hand its place to a scene already disposed,
        // which is a viewer gone white with nothing left to say why.
        if (inFlight.current !== mine) return;
        // The one it replaces goes now, and not a moment before: this is the frame where
        // there is something to put in its place.
        before?.();
        if (standing.current === before) standing.current = null;
        inFlight.current = null;
        // A MESH THAT WOULD NOT LOAD KEEPS NOTHING. Its layer is empty and the message below
        // is what the panel shows — the same as before this change. Holding the old part
        // under a sentence saying this one could not be read would be a viewer showing one
        // mesh and talking about another.
        if (s === 'ready') {
          standing.current = mine;
        } else {
          mine?.();
          if (view.current) clearPartScene(view.current);
          view.current = null;
        }
        shownAt.current = slot;
        setShown(slot);
        setSwapping(false);
        setState(s);
        if (s === 'ready') setScenes((n) => n + 1);
      },
    });
    // ONCE, so it does not matter how many places reach for it: the cleanup below, the
    // unmount sweep, and the swap above can each be the one that gets there first.
    mine = once(raw);
    inFlight.current = mine;
    return () => {
      // ONLY THE ONE THIS EFFECT BUILT, AND ONLY WHILE IT IS STILL LOADING. Once it is what
      // is on screen it belongs to `standing`, and taking it down here — which is what this
      // cleanup used to do — is the blank this whole arrangement exists to avoid. A second
      // edit landing on top of the first goes through here, which is what caps this viewer at
      // two live scenes however fast the buttons are pressed.
      if (inFlight.current === mine) {
        mine?.();
        inFlight.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, words, extentWords, faceWords, wantedWords]);

  // AND EVERYTHING GOES WHEN THE PANEL DOES. The effect above deliberately outlives its own
  // deps changing, so it cannot be what frees the scene on screen; this can, and it runs
  // exactly once, at the end.
  useEffect(() => { keyNow.current = keepView; });
  useEffect(() => () => {
    // WHERE IT WAS LEFT, TAKEN ON THE WAY OUT. This viewer is unmounted by expanding it —
    // the panel's is torn down while the modal is up, and the modal's when it closes — so
    // without this the camera was kept across a mesh swap and lost across the one gesture
    // somebody makes precisely to go on looking at the same thing.
    if (keyNow.current && view.current) {
      held = { key: keyNow.current, ...view.current.viewpoint(), zoom: zoomNow.current };
    }
    inFlight.current?.();
    standing.current?.();
    inFlight.current = null;
    standing.current = null;
  }, []);

  // WHAT THE MODEL SAID ABOUT EACH FACE, onto the box. Re-sent when the answer changes and
  // when the scene is rebuilt: a fresh scene draws six plates with nothing under them.
  //
  // AND NOTHING AT ALL WHILE THE CALL IS OUT. Not the previous answer, and not the six names
  // either: every plate on this box is a claim about which way round the part is, and while
  // the question is being asked again there is nothing here that is known — see FacesTold.
  useEffect(() => {
    if (orientAsking) view.current?.said('part', ASKING);
    else view.current?.said('part', orientJobId == null && !contactFace ? null : faceSaid);
  }, [faceSaid, orientJobId, orientAsking, scenes]);

  // Moved and turned, never re-framed: the part pivots about its own centre, so the shot that
  // held it still holds it — and a camera that re-fitted on every typed degree would be a
  // picture sliding under the hand typing.
  useEffect(() => { view.current?.show(asm, 1, 1); }, [asm, scenes]);
  // The orange rim, on the part whose card is open.
  useEffect(() => {
    view.current?.select(bare ? null : picked);
  }, [picked, scenes, bare]);
  const highlightKey = `${(highlight ?? []).join(',')}|${(moving ?? []).join(',')}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    view.current?.highlight([...(bare ? [] : highlight ?? []), ...(moving ?? [])]);
  }, [highlightKey, scenes, bare]);
  useEffect(() => { view.current?.draw(mode); }, [mode, scenes]);
  const overlaysKey = (overlays ?? []).map((o) => `${o.url}#${o.colour}#${(o.on ?? []).join(",")}`).join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { view.current?.overlays(overlays ?? []); }, [overlaysKey, scenes]);
  const collideNow = useRef(collide);
  collideNow.current = collide;
  // MEASURED ON THE SCENE THAT HOLDS WHAT IS ASKED ABOUT. New covers are new parts, and a new
  // scene is built for them: asked of the old one in the meantime, every pair naming a new cover
  // was skipped and "nothing overlaps" came back — the answer the covers call's second pass was
  // then told. The new scene's arrival (`scenes`) asks again.
  useEffect(() => {
    const ask = collideNow.current;
    if (!ask || !view.current || sceneOthers.current !== othersIds) return;
    let gone = false;
    void view.current.overlapsAmong(asm, ask.pairs).then((found) => { if (!gone) ask.onFound(found); });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collide?.key, scenes]);
  const paintKey = Object.entries(clashPaint ?? {}).map(([id, faces]) => `${id}:${faces.length}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { view.current?.clashMarks(clashPaint ?? null); }, [paintKey, scenes]);
  useEffect(() => { view.current?.light(lit); rememberLight(lit); }, [lit, scenes]);
  useEffect(() => { view.current?.hide(hidden); }, [hidden, scenes]);
  useEffect(() => { view.current?.hidePieces('part', piecesOff); }, [piecesOff, scenes]);
  useEffect(() => { view.current?.boxPieces('part', bare ? [] : piecesBoxed); }, [piecesBoxed, scenes, bare]);
  const dimensionFocus = useDimensionFocus();
  // WHICH GROUPS OF MEASUREMENTS ARE DRAWN — none until somebody asks: a mesh's own with its name,
  // the rest from a row each in the cards (see measureGroups). They were on with the part's box,
  // which is on by default, and a model opened under forty red numbers.
  const [linesOn, setLinesOn] = useState<string[]>([]);
  // THE ROWS: every group the dimensions have that is not one mesh's name — those are on the
  // mesh rows already — in the order the dimensions panel reads them.
  const measureGroups = useMemo(() => {
    if (!dimensionLines) return undefined;
    const groups = [...new Set(Object.values(dimensionLines.about).map((a) => a.group))]
      .filter((g) => !pieces.includes(g))
      .sort(compareGroups);
    return groups.map((title) => ({ title, on: linesOn.includes(title) }));
  }, [dimensionLines, pieces, linesOn]);
  useEffect(() => {
    view.current?.dimensionLines('part', dimensionLines && !bare ? {
      ...dimensionLines,
      on: linesOn,
      focus: dimensionFocus && dimensionLines.about[dimensionFocus] ? dimensionFocus : null,
    } : null);
  }, [dimensionLines, linesOn, dimensionFocus, scenes, bare]);
  // SPREAD OR TOGETHER — slid between the two when it is the switch that changed, put straight there
  // when it is the scene: a new mesh arrives the way the switch already stands.
  const explodedAt = useRef(-1);
  // AND TOGETHER WHEREVER THE SWITCH IS NOT OFFERED. `exploded` is one flag for the whole app
  // (usePartMarks), so a product left spread apart used to open the next PART spread apart too —
  // with no switch on that viewer to put it back, the mesh being in pieces and the control that
  // did it a screen away. A viewer that does not ask the question does not answer it either.
  const spread = !!explodable && exploded;
  useEffect(() => {
    view.current?.explode('part', spread, explodedAt.current === scenes);
    explodedAt.current = scenes;
  }, [spread, scenes]);

  // EVERYTHING DRAWN ABOUT THE PART, OUT OR BACK — the box and its numbers, the six face
  // names, the origin cross and the gizmo. The assembly phase's own button (see marks there),
  // for the same reason: what a mesh is finally judged on is the surface, and eight labels,
  // six arrows and a cross are between you and it.
  //
  // Derived rather than stored, like the phase's: the card and the gizmo button own these
  // switches, and a second flag beside them would disagree with them the moment one cross
  // came back on.
  const marks = (on: boolean) => {
    const ids = on ? [] : ['part'];
    // All four at once, which is what the store takes: the button asks one question. Not in a
    // joint, whose parts' switches are its own.
    setPartMarks(joint ? { gizmoOn: on }
      : { rulersOff: ids, facesOff: ids, boxesOff: ids, gizmoOn: on });
    const all = on ? otherIds : [];
    setOthersOn({ rulers: all, faces: all, boxes: all });
  };
  const marksOn = gizmoOn || (joint
    ? Object.values(othersOn).some((on) => on.length > 0)
    : !rulersOff.length || !facesOff.length || !boxesOff.length);
  // THE MARKS AS THE SWITCHES STAND, onto a scene: on every switch, and on a new scene THE MOMENT
  // IT IS READY (see onReady), before it draws a frame. A scene is born with every mark on, and
  // left to an effect after React's next render it drew a few frames that way — every box, ruler and
  // face word flashing over a joint whose switches are all off.
  const applyMarks = useRef<(v: AssemblyView) => void>(() => {});
  applyMarks.current = (v) => {
    v.measures(bare ? [] : onOf('rulers', rulersOff));
    v.faces(bare ? [] : onOf('faces', facesOff));
    v.boxes(bare ? [] : onOf('boxes', boxesOff));
    v.gizmo(gizmoOn && !bare);
  };
  useEffect(() => {
    if (view.current) applyMarks.current(view.current);
  }, [rulersOff, facesOff, boxesOff, gizmoOn, othersOn, scenes, bare]);



  // OFF WHAT THE LIST HOLDS WHEN THE PRESS LANDS: one press on a merged row flips several.
  const toggle = (set: Dispatch<SetStateAction<string[]>>) => (id: string) => (
    set((was) => (was.includes(id) ? was.filter((x) => x !== id) : [...was, id])));


  // THE FOUR CONTROLS ON THE PICTURE, made once and drawn either over the viewport or in a row
  // above it (`controlsAbove`). Each only once the parts are up, as before.
  const ready = state === 'ready';
  const modeToggle = ready && (
    <SegmentedToggle options={MODES} value={mode} onChange={pickMode} className="shadow-sm" small={beside} />
  );
  const poseToggles = ready && (
    <PoseToggles
      side={sides ? side : undefined}
      onSide={setPartSide}
      // AN ASSEMBLY CAN BE SPREAD APART; A PART CANNOT. It was offered wherever the FILE held
      // more than one mesh, which is a fact about how the file was built and not about what it
      // is of: a barrel nut converted from a supplier's CAD arrives as two bodies, and the
      // toggle sat over it offering to explode a nut into the halves of its own thread. What
      // spreading apart means is "show the components of this thing separately", and only the
      // caller knows whether the thing HAS components — see `explodable`.
      exploded={apart ? apart.on : explodable && pieces.length > 1 ? exploded : undefined}
      onExploded={(on) => (apart ? apart.set(on) : setPartMarks({ exploded: on }))}
      inline={controlsAbove}
    />
  );
  const viewControls = ready && (
    <ViewControls
      sink={viewAngles}
      onHome={() => view.current?.home()}
      onTurn={(axis, deg) => view.current?.turn(axis, deg)}
      gizmoOn={gizmoOn}
      onToggleGizmo={() => setGizmoOn(!gizmoOn)}
      // AND THE SIX NUMBERS TYPED IN, as the assembly phase's are: the shot goes
      // exactly there. Without this the rows are readouts that LOOK typed into — the
      // boxes take a number and the commit has nowhere to send it — and a field that
      // accepts a 30 and leaves the camera where it was is worse than a printed angle.
      // Same scene behind it, so it is the same one call.
      onLook={(shot) => view.current?.look(shot)}
    />
  );
  const toggleMarks = () => marks(!marksOn);

  // And the same card, with the same three switches. One of them, because there is one part — the card
  // is a list in the assembly phase because a fitting is several. Over the viewport's top right, or
  // beside it where the controls are out of the picture (`controlsAbove`).
  const cards = (
        <ScenePartCards
          parts={[
            { instanceId: 'part', label: label || t('notice.3d.boxView') },
            ...(others ?? []).map((o) => ({ instanceId: o.id, label: o.label, group: o.group })),
          ]}
          hidden={hidden}
          onToggle={toggle(setHidden)}
          pieces={pieces}
          pieceLabel={pieceLabel}
          pieceOrder={pieceOrder}
          piecesOff={piecesOff}
          onTogglePiece={toggle(setPiecesOff)}
          piecesBoxed={piecesBoxed}
          // A MESH'S NAME DRAWS ITS BOX AND ITS OWN MEASUREMENTS, together — both on or both off.
        onBoxPiece={(name) => {
          const on = !piecesBoxed.includes(name);
          setPiecesBoxed(on ? [...piecesBoxed, name] : piecesBoxed.filter((x) => x !== name));
          setLinesOn(on ? [...linesOn, name] : linesOn.filter((x) => x !== name));
        }}
          rulersOff={offOf('rulers', rulersOff)}
          onToggleRuler={switchOf('rulers', rulersOff, setRulersOff)}
          facesOff={offOf('faces', facesOff)}
          onToggleFaces={switchOf('faces', facesOff, setFacesOff)}
          boxesOff={offOf('boxes', boxesOff)}
          onToggleBox={switchOf('boxes', boxesOff, setBoxesOff)}
        measureGroups={measureGroups}
        onToggleMeasureGroup={toggle(setLinesOn)}
        inline={outside}
          marksOn={marksOn}
          // IN THE CORNER'S ROW, beside the draw mode, where the picture's controls are on it.
          below={!outside}
          picked={picked}
          onPick={setPicked}
        />
  );

  return (
    <div className="flex w-full flex-col items-center gap-2">
      {/* HOW IT IS LIT, above the picture. Its companion — how it is DRAWN — has moved into
          the viewport's own corner, and this one has not: even against studio is a question
          about the LIGHT a surface is being judged under, asked while comparing a render with
          a swatch beside the page, and it is offered on two pages out of the app. The draw
          mode is about the picture and is offered everywhere, so it lives on the picture. */}
      {lights && (
        <SegmentedToggle
          options={[{ value: 'even', label: 'Even' }, { value: 'studio', label: 'Studio' }]}
          value={lit}
          onChange={setLit}
        />
      )}
      {/* THE PICTURE'S CONTROLS OVER IT, not on it, where the caller asks: the camera readout
          left, and on the right the pose, the draw mode and the marks switch STACKED (Louis
          2026-10-29), one under the other rather than a row across the picture. */}
      {controlsAbove && (
        // CENTRED ON A PHONE, the readout above the stack.
        <div className="flex w-full flex-wrap items-start justify-center gap-3 sm:grid sm:grid-cols-[1fr_auto]">
          <div className="justify-self-start">{viewControls}</div>
          <div className="flex flex-col items-center gap-2 sm:justify-self-end">
            {poseToggles}
            {modeToggle}
            {ready && <MarksButton on={marksOn} onToggle={toggleMarks} />}
          </div>
        </div>
      )}
      {/* MARKED as a place a mesh is drawn — see VIEWPORT_MARK, which says what reads it and
          why the mark is on the panel rather than on the canvas. This is the viewer the turn
          dialog opens over. */}
      {/* A DRAWN EDGE, and not only a rounded corner. The viewport is white on a white
          panel, so with nothing but a radius it has no boundary at all: the object floats
          in the page and the controls in its corners read as the page's rather than as
          this box's. A hairline says where the picture stops. */}
      {/* BESIDE, AND THE PICTURE STILL CENTRED (Louis 2026-10-28): the column's width taken off both
          sides, so the viewport stands where it stood, the column in the room on its right. */}
      <div className={controlsAbove ? 'flex w-full flex-col gap-3' : beside ? 'relative w-full' : 'contents'}>
      <div
        {...VIEWPORT_MARK}
        className={`relative overflow-hidden rounded-xl border border-slate-200 bg-white ${className} ${beside ? 'mx-auto !w-[calc(100%-30rem)]' : ''}`}
      >
      {/* Both layers are always mounted and always the full box: a scene measures itself off
          its element, and a layer that only got its size at the moment it was revealed would
          be a mesh framed for a box it was not in. */}
      {hosts.map((ref, i) => (
        <div
          key={i}
          ref={ref}
          className={`absolute inset-0 ${shown === i ? '' : 'invisible'}`}
        />
      ))}
      {/* HOW IT IS DRAWN, IN THE PICTURE IT IS ABOUT — the assembly phase's own arrangement,
          and here for its reasons: it changes no byte of the file, and a control for a picture
          belongs on the picture. Left edge flush with the card below it, on the card's own
          ground, so the corner reads as one block.
          SHOWN IN BOTH VIEWS, unlike the counts beside it. Whether a reconstruction spent its
          triangles well is a question for somebody judging one, which is what `topology`
          gates; whether the texture is hiding the geometry is a question anybody looking at a
          part may want to ask, and it costs a button. */}
      {modeToggle && !outside && (
        // ONE ROW IN THE CORNER, the draw mode and the marks switch, and the cards under it. The
        // toggle stood a fixed width in from the right, clear of a marks button that was one
        // icon: as two labels the switch grew under it (Louis 2026-10-28).
        <div className="absolute right-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-wrap items-start justify-end gap-2">
          {modeToggle}
          <MarksButton on={marksOn} onToggle={toggleMarks} />
        </div>
      )}
      {/* WHICH HAND, AND ASSEMBLED OR EXPLODED — top centre, each only where it has two true
          positions. See PoseToggles. */}
      {!controlsAbove && poseToggles}
      {/* THE PARTS DB SWITCH, BOTTOM RIGHT. It was top left, which is where the camera
          readout stands — two boxes over one corner, and the switch drawn across the numbers.
          It is the one control here that is about the mesh as an ASSET rather than about the
          picture, so it belongs where nothing is being read: the far corner, out of the way
          of the readout, the cards and the object between them. */}
      {corner && (
        <div className="absolute bottom-3 right-3 z-10">{corner}</div>
      )}

      {/* SOMETHING IS COMING, said over whatever is on screen.
          The scene being replaced stays up now (see the mount effect), and left to itself that
          is a viewer that looks finished for the several seconds a drilled mesh takes to come
          back — the part is right there, unchanged, and nothing says the picture is about to
          be a different one. So the spinner fires for both: an empty viewport waiting for its
          first mesh, and a part waiting to be replaced by itself with a hole in it.
          ON A DISC, because it is no longer only ever drawn against white. Over dark wood a
          pale grey spinner is not there at all, and this one has to be legible over any part
          the viewer might be holding.
          POINTER-EVENTS-NONE: the part underneath is still turnable while its replacement
          loads, and a curtain that stopped the mouse would take away the one thing keeping
          the old scene up was for. */}
      {(state === 'loading' || swapping) && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-white/85 p-3 shadow-sm backdrop-blur-sm">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </span>
        </div>
      )}
      {state === 'failed' && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
          {t('notice.assembly.loadFailed')}
        </div>
      )}

      {/* The same corner box the assembly phase reads its shot from — home, a turn about each
          axis, and where the camera is standing. No pan: there is one part, it is at the
          origin, and the origin is what the shot is centred on.
          TOP LEFT, where that phase puts it, and not only for the look of it: the part cards
          are pinned to the right in the component that draws them, so a readout on the right
          is a readout underneath them. */}
      {/* Transparent to the pointer so the viewport orbits under it, with the control itself
          taking its events back — the assembly phase's own arrangement, and for its reason.
          Only once the parts are up: these turn a camera, and a readout of an angle nothing
          is looking from is a number about nothing. */}
      {viewControls && !outside && (
        <div className="pointer-events-none absolute left-3 top-3 flex flex-col items-start gap-2">
          <div className="pointer-events-auto">{viewControls}</div>
        </div>
      )}

      {!outside && cards}

      {/* FETCH THE MESH AGAIN, without taking the page down with it.
          A reconstruction is megabytes over a connection that sometimes drops one, and what
          is left when it does is a white rectangle saying the model could not be loaded —
          with the panel around it intact and every answer in it still true. Reloading the
          whole page to get one file was the only way back, and it costs every other thing
          on screen: the phase's scroll position, which mesh was open, what the modal was
          showing.
          BOTTOM LEFT, the one free corner. The readout is top left, the cards are right, and
          the two controls at the bottom right are about this mesh as an asset and as a
          picture. This one is about the FETCH, which is nothing to do with any of them.
          Always there, and not only after a failure: a mesh that arrived wrong — half a
          texture, a load that finished on a stale file — looks like a mesh, and the button
          that would fix it must not be the one that appears when the app already knows. */}
      <button
        type="button"
        onClick={() => setAttempt((n) => n + 1)}
        aria-label={t('notice.3d.reload')}
        title={t('notice.3d.reload')}
        className="absolute bottom-3 left-3 rounded-lg border border-slate-300 bg-white/90 p-2 text-slate-500 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600"
      >
        {/* AND IT IS THE VIEWER'S ONLY SIGN OF WORK once a part is on screen. A mesh being
            replaced — drilled, scaled, a surface closed — leaves the old one up rather than
            blanking the panel, so the spinner in the middle never fires for it; this turns,
            in the corner, where it costs nobody their picture. It is the honest place for it
            too: what is happening is a fetch, and this button is the fetch. */}
        <RotateCw className={`h-4 w-4 ${state === 'loading' || swapping ? 'animate-spin' : ''}`} />
      </button>

      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          aria-label={t('notice.3d.expand')}
          className={`absolute bottom-3 rounded-lg border border-slate-300 bg-white/90 p-2 text-slate-500 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600 ${corner ? 'right-16' : 'right-3'}`}
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      )}
      </div>
      {controlsAbove && cards}
      {/* Half way up the picture, in the room on its left (Louis 2026-10-29). */}
      {beside && left && (
        <div className="absolute inset-y-0 left-0 flex w-[14.25rem] items-center justify-center">{left}</div>
      )}
      {beside && (
        <div className="absolute right-0 top-0 flex max-h-full w-[14.25rem] flex-col items-center gap-2">
          {viewControls}
          {modeToggle}
          {ready && <MarksButton on={marksOn} onToggle={toggleMarks} small />}
          {cards}
        </div>
      )}
      </div>
      {!beside && left}

      {/* WHAT THE MODEL SAID ABOUT THE FACE THAT WAS PRESSED, and where it disagrees with the
          rest of its own answer. The same window the answer panel under this viewer opens, so
          the two say one thing: see OrientViews.

          Renders nothing where that face was never answered, which is why the plates can be
          pressable without anything having to know which of them carry a word. */}
      {askedFace && (
        <OrientFaceWhy
          answer={orient}
          face={askedFace}
          onClose={() => setAskedFace(null)}
        />
      )}
    </div>
  );
}
